import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { sans, mono, display, green, amber, useTheme, useCurrency } from '../tokens';
import {
  approvedCashByQuarter,
  computeCashflow, computeQuarterEnds,
  YEARS, MONTHS, OPENING_CASH_2026,
} from '../data/cashflowForecast';
import { subscribe as subscribeOverrides } from '../data/forecastOverrides';

// ─── Currency helpers ────────────────────────────────────────────────────────
// Source data is USD. CAD toggle multiplies by fxRate (CAD per USD, ~1.37).
const usdToDisplay = (n, cc) => {
  if (n == null) return n;
  if (!cc || cc.currency === 'USD') return n;
  return n * (cc.fxRate || 1);
};

const fmtConverted = (v) => {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(v / 1_000)}K`;
  return `$${Math.round(v)}`;
};

const fmtUsdSource = (n, cc) => fmtConverted(usdToDisplay(n, cc));

// ─── Cash Forecast Chart ─────────────────────────────────────────────────────
// Per design spec: flat card, hairline border, single subtle wash, hover-only
// crosshair + dot, edge-pinned readout. Strip back, don't decorate.
function QuarterlyCashChart({ forecast, approved, currencyLabel }) {
  const t = useTheme();
  const cc = useCurrency();
  const [hoveredIdx, setHoveredIdx] = useState(null);

  // Convert source USD → display currency once
  const fc = forecast.map((p) => ({ ...p, cash: usdToDisplay(p.cash, cc) }));
  const ap = approved.map((p) => ({ ...p, cash: usdToDisplay(p.cash, cc) }));

  const labels = fc.map((p) => p.label);
  const allValues = [...fc.map((p) => p.cash), ...ap.map((p) => p.cash)];
  const max = Math.max(...allValues, 0) * 1.08;
  const min = 0;

  // Geometry — design spec: 100% wide, fixed 300 height
  const W = 920;
  const H = 300;
  const PAD_L = 56;
  const PAD_R = 16;
  const PAD_T = 28;
  const PAD_B = 36;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const xFor = (i) => PAD_L + (i / Math.max(labels.length - 1, 1)) * innerW;
  const yFor = (v) => PAD_T + (1 - (v - min) / (max - min)) * innerH;

  // Y ticks — 4 nice round numbers
  const yTicks = useMemo(() => {
    const range = max - min;
    if (range === 0) return [0];
    const targetTicks = 4;
    const rawStep = range / targetTicks;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const niceStep = Math.ceil(rawStep / magnitude) * magnitude;
    const ticks = [];
    for (let v = 0; v <= max + niceStep / 2; v += niceStep) ticks.push(v);
    return ticks;
  }, [max, min]);

  // Year boundaries — drawn as text only, no vertical lines per spec
  const yearLabels = useMemo(() =>
    labels.map((lbl, i) => {
      const parts = lbl.split(' ');
      return parts[0] === 'Q1' ? { x: xFor(i), label: parts[1] } : null;
    }).filter(Boolean),
    [labels] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Path builders — straight segments only, per user request
  const buildStraightPath = (points) => {
    if (points.length < 2) return '';
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  };

  const fcPts = fc.map((p, i) => ({ x: xFor(i), y: yFor(p.cash) }));
  const fcPath = buildStraightPath(fcPts);
  const fcFillPath = fcPts.length > 1
    ? `${fcPath} L ${fcPts[fcPts.length - 1].x} ${PAD_T + innerH} L ${fcPts[0].x} ${PAD_T + innerH} Z`
    : '';

  const apPts = ap
    .map((p) => {
      const i = labels.indexOf(p.label);
      return i >= 0 ? { x: xFor(i), y: yFor(p.cash), label: p.label, cash: p.cash } : null;
    })
    .filter(Boolean);
  const apPath = buildStraightPath(apPts);

  // Series B annotation — single hairline + label, not a billboard
  const seriesBIdx = useMemo(() => {
    let bestIdx = -1, biggestJump = 0;
    fc.forEach((p, i) => {
      if (i === 0) return;
      const jump = p.cash - fc[i - 1].cash;
      if (jump > biggestJump) { biggestJump = jump; bestIdx = i; }
    });
    return biggestJump > (max - min) * 0.2 ? bestIdx : -1;
  }, [fc, max, min]);

  // Edge-pinned readout — always shows ending at rest, hovered value when hovering
  const readout = (() => {
    const idx = hoveredIdx ?? (fc.length - 1);
    const point = fc[idx];
    const apPoint = ap.find((a) => a.label === point.label);
    return { idx, point, apPoint, isResting: hoveredIdx == null };
  })();

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', overflow: 'visible' }} preserveAspectRatio="xMidYMid meet">
      {/* Currency unit — top-left of y-axis */}
      <text x={PAD_L - 8} y={PAD_T - 12} fontSize={9} fontFamily={sans} fill={t.textFaint} textAnchor="end" letterSpacing={0.5} fontWeight={500}>
        {currencyLabel}
      </text>

      {/* Horizontal gridlines — barely there */}
      {yTicks.map((v, i) => (
        <line
          key={`h-${i}`}
          x1={PAD_L} y1={yFor(v)} x2={PAD_L + innerW} y2={yFor(v)}
          stroke="rgba(15,42,36,0.05)" strokeWidth={1}
        />
      ))}

      {/* Baseline at y=0 — slightly stronger */}
      <line
        x1={PAD_L} y1={yFor(0)} x2={PAD_L + innerW} y2={yFor(0)}
        stroke="rgba(15,42,36,0.12)" strokeWidth={1}
      />

      {/* Y-axis labels — Inter, not mono */}
      {yTicks.map((v, i) => {
        const lbl = v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(0)}M` : v >= 1_000 ? `$${Math.round(v / 1_000)}K` : `$${Math.round(v)}`;
        return (
          <text
            key={`yl-${i}`}
            x={PAD_L - 10} y={yFor(v) + 3}
            fontSize={10} fontFamily={sans} fill={t.textFaint}
            textAnchor="end" fontWeight={500}
          >
            {lbl}
          </text>
        );
      })}

      {/* X-axis year labels only — no per-quarter clutter */}
      {yearLabels.map((yl, i) => (
        <text
          key={`xl-${i}`} x={yl.x} y={H - 12}
          fontSize={11} fontFamily={sans} fill={t.textMuted}
          textAnchor="middle" fontWeight={500} letterSpacing={0.3}
        >
          {yl.label}
        </text>
      ))}

      {/* Forecast wash — single flat tint, opacity 0.05 */}
      {fcPts.length > 1 && <path d={fcFillPath} fill={green} opacity={0.05} />}

      {/* Forecast line — solid, 1.5px, no shadow */}
      <path d={fcPath} fill="none" stroke={green} strokeWidth={1.5} strokeLinejoin="round" />

      {/* Board-approved line — dashed, 1.5px, 0.85 opacity */}
      {apPts.length > 1 && (
        <path d={apPath} fill="none" stroke={amber} strokeWidth={1.5} strokeDasharray="4 3" strokeLinejoin="round" opacity={0.85} />
      )}

      {/* Series B — single thin hairline + label */}
      {seriesBIdx > 0 && (
        <g>
          <line
            x1={xFor(seriesBIdx)} y1={yFor(fc[seriesBIdx].cash) - 8}
            x2={xFor(seriesBIdx)} y2={yFor(0)}
            stroke="rgba(15,42,36,0.15)" strokeWidth={1}
          />
          <text
            x={xFor(seriesBIdx)} y={yFor(fc[seriesBIdx].cash) - 16}
            fontSize={10} fontFamily={sans} fill={t.textSecondary}
            textAnchor="middle" fontWeight={500}
          >
            Series B · {fc[seriesBIdx].label}
          </text>
        </g>
      )}

      {/* Hover crosshair — solid 1px line, opacity 0.18 */}
      {hoveredIdx != null && (
        <line
          x1={xFor(hoveredIdx)} y1={PAD_T} x2={xFor(hoveredIdx)} y2={PAD_T + innerH}
          stroke="rgba(15,42,36,0.18)" strokeWidth={1}
        />
      )}

      {/* Hover dots — only on the active quarter */}
      {hoveredIdx != null && (
        <>
          <circle
            cx={xFor(hoveredIdx)} cy={yFor(fc[hoveredIdx].cash)}
            r={4} fill={green} stroke="#FFFBF6" strokeWidth={2}
          />
          {(() => {
            const a = ap.find((x) => x.label === fc[hoveredIdx].label);
            return a ? (
              <circle cx={xFor(hoveredIdx)} cy={yFor(a.cash)} r={4} fill="none" stroke={amber} strokeWidth={2} />
            ) : null;
          })()}
        </>
      )}

      {/* Hit areas — full-height vertical strips */}
      {fc.map((p, i) => (
        <rect
          key={`hit-${i}`}
          x={xFor(i) - innerW / Math.max(labels.length - 1, 1) / 2}
          y={PAD_T}
          width={innerW / Math.max(labels.length - 1, 1)}
          height={innerH}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onMouseEnter={() => setHoveredIdx(i)}
          onMouseLeave={() => setHoveredIdx(null)}
        />
      ))}

      {/* Edge-pinned readout — top-right of plot area */}
      <g style={{ pointerEvents: 'none' }}>
        <text
          x={PAD_L + innerW - 4} y={PAD_T + 2}
          fontSize={10} fontFamily={sans} fill={t.textMuted}
          textAnchor="end" letterSpacing={0.6} fontWeight={600}
        >
          {readout.point.label.toUpperCase()}
        </text>
        <text
          x={PAD_L + innerW - 4} y={PAD_T + 22}
          fontSize={18} fontFamily={mono} fill={t.textPrimary}
          textAnchor="end" fontWeight={600}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {fmtConverted(readout.point.cash)}
        </text>
        {readout.apPoint && (
          <text
            x={PAD_L + innerW - 4} y={PAD_T + 38}
            fontSize={11} fontFamily={sans} fill={amber}
            textAnchor="end" fontWeight={500}
          >
            Budget {fmtConverted(readout.apPoint.cash)}
          </text>
        )}
      </g>
    </svg>
  );
}

// ─── Cashflow table — quarterly (default), monthly, or annual (all years) ────
function CashflowTable({ year, cf, granularity = 'quarterly' }) {
  const t = useTheme();
  const cc = useCurrency();

  const isMonthly = granularity === 'monthly';
  const isAnnual = granularity === 'annual';

  // Build columns: 6 years (annual), 12 months (monthly), or 4 quarters (quarterly)
  let columns;
  if (isAnnual) {
    columns = YEARS.map((y) => {
      const yMonths = cf.filter((m) => m.year === y);
      if (yMonths.length === 0) return null;
      return {
        label: `FY${y}`,
        opening: yMonths[0].opening,
        closing: yMonths[yMonths.length - 1].closing,
        interestIncome: yMonths.reduce((s, m) => s + m.inflows.interestIncome, 0),
        seriesB: yMonths.reduce((s, m) => s + m.inflows.seriesB, 0),
        srEdRefund: yMonths.reduce((s, m) => s + m.inflows.srEdRefund, 0),
        grant: yMonths.reduce((s, m) => s + m.inflows.grant, 0),
        other: yMonths.reduce((s, m) => s + m.inflows.other, 0),
        totalInflows: yMonths.reduce((s, m) => s + m.totalInflows, 0),
        burn: yMonths.reduce((s, m) => s + m.outflows.burn, 0),
        loanPayment: yMonths.reduce((s, m) => s + m.outflows.loanPayment, 0),
        totalOutflows: yMonths.reduce((s, m) => s + m.totalOutflows, 0),
        netChange: yMonths.reduce((s, m) => s + m.netChange, 0),
      };
    }).filter(Boolean);
    if (columns.length === 0) return null;
  } else {
    const months = cf.filter((m) => m.year === year);
    if (months.length === 0) return null;
    columns = isMonthly
      ? months.map((m, i) => ({
          label: MONTHS[i],
          opening: m.opening,
          closing: m.closing,
          interestIncome: m.inflows.interestIncome,
          seriesB: m.inflows.seriesB,
          srEdRefund: m.inflows.srEdRefund,
          grant: m.inflows.grant,
          other: m.inflows.other,
          totalInflows: m.totalInflows,
          burn: m.outflows.burn,
          loanPayment: m.outflows.loanPayment,
          totalOutflows: m.totalOutflows,
          netChange: m.netChange,
        }))
      : [0, 1, 2, 3].map((qi) => {
          const slice = months.slice(qi * 3, qi * 3 + 3);
          return {
            label: `Q${qi + 1}`,
            opening: slice[0].opening,
            closing: slice[slice.length - 1].closing,
            interestIncome: slice.reduce((s, m) => s + m.inflows.interestIncome, 0),
            seriesB: slice.reduce((s, m) => s + m.inflows.seriesB, 0),
            srEdRefund: slice.reduce((s, m) => s + m.inflows.srEdRefund, 0),
            grant: slice.reduce((s, m) => s + m.inflows.grant, 0),
            other: slice.reduce((s, m) => s + m.inflows.other, 0),
            totalInflows: slice.reduce((s, m) => s + m.totalInflows, 0),
            burn: slice.reduce((s, m) => s + m.outflows.burn, 0),
            loanPayment: slice.reduce((s, m) => s + m.outflows.loanPayment, 0),
            totalOutflows: slice.reduce((s, m) => s + m.totalOutflows, 0),
            netChange: slice.reduce((s, m) => s + m.netChange, 0),
          };
        });
  }

  const sumCol = (key) => columns.reduce((s, c) => s + c[key], 0);
  const yearTotals = {
    interestIncome: sumCol('interestIncome'),
    seriesB: sumCol('seriesB'),
    srEdRefund: sumCol('srEdRefund'),
    grant: sumCol('grant'),
    other: sumCol('other'),
    totalInflows: sumCol('totalInflows'),
    burn: sumCol('burn'),
    loanPayment: sumCol('loanPayment'),
    totalOutflows: sumCol('totalOutflows'),
    netChange: sumCol('netChange'),
  };

  const inflowRows = [
    { key: 'interestIncome', label: 'Interest income (GIC ~4%)' },
    { key: 'seriesB', label: 'Series B proceeds' },
    { key: 'srEdRefund', label: 'SR&ED refund' },
    { key: 'grant', label: 'Grants' },
    { key: 'other', label: 'Other inflows' },
  ].filter((r) => yearTotals[r.key] !== 0);

  const outflowRows = [
    { key: 'burn', label: 'Operating burn' },
    { key: 'loanPayment', label: 'Loan payment' },
  ].filter((r) => yearTotals[r.key] !== 0);

  const labelCell = { padding: '8px 14px', fontFamily: sans, fontSize: 12, color: t.textSecondary };
  const labelCellIndent = { ...labelCell, paddingLeft: 28, color: t.textMuted, fontSize: 11 };
  const numCell = { padding: '8px 14px', fontFamily: mono, fontSize: 12, textAlign: 'right', color: t.textPrimary, fontVariantNumeric: 'tabular-nums' };
  const numCellMuted = { ...numCell, color: t.textMuted, fontSize: 11 };
  const sectionRow = { padding: '14px 14px 4px', fontSize: 9, color: t.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: sans };

  const renderNum = (n, opts = {}) => {
    if (n == null || n === 0) return <span style={{ color: t.textFaint }}>—</span>;
    const v = usdToDisplay(n, cc);
    const negative = v < 0;
    return (
      <span style={{ color: negative ? '#9A3A2A' : (opts.muted ? t.textMuted : t.textPrimary) }}>
        {fmtUsdSource(n, cc)}
      </span>
    );
  };

  // Monthly view is wider — wrap in a horizontal scroll container
  const colSpanTotal = columns.length + 2;
  const lastCol = columns[columns.length - 1];
  const headerLabel = isAnnual ? `FY${YEARS[0]}–FY${YEARS[YEARS.length - 1]}` : year;
  const totalLabel = isAnnual ? '6yr Total' : 'Year';

  return (
    <div style={{ overflowX: isMonthly ? 'auto' : 'visible' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMonthly ? 980 : undefined }}>
        <thead>
          <tr style={{ background: t.bgSurface, borderBottom: `1px solid ${t.border}` }}>
            <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, fontFamily: sans, minWidth: 200, width: isMonthly ? 220 : '38%' }}>
              {headerLabel}
            </th>
            {columns.map((c) => (
              <th key={c.label} style={{ textAlign: 'right', padding: '10px 14px', fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, fontFamily: sans }}>
                {c.label}
              </th>
            ))}
            <th style={{ textAlign: 'right', padding: '10px 14px', fontSize: 10, fontWeight: 700, color: t.textPrimary, textTransform: 'uppercase', letterSpacing: 0.6, fontFamily: sans, background: t.bgCard }}>
              {totalLabel}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ background: t.bgCard }}>
            <td style={{ ...labelCell, fontWeight: 600, color: t.textPrimary }}>Opening cash</td>
            {columns.map((c) => <td key={c.label} style={numCell}>{renderNum(c.opening)}</td>)}
            <td style={{ ...numCell, fontWeight: 600, background: t.bgSurface }}>{renderNum(columns[0].opening)}</td>
          </tr>
          <tr><td colSpan={colSpanTotal} style={sectionRow}>Inflows</td></tr>
          {inflowRows.map((r) => (
            <tr key={r.key}>
              <td style={labelCellIndent}>{r.label}</td>
              {columns.map((c) => <td key={c.label} style={numCellMuted}>{renderNum(c[r.key], { muted: true })}</td>)}
              <td style={{ ...numCell, background: t.bgSurface }}>{renderNum(yearTotals[r.key])}</td>
            </tr>
          ))}
          <tr style={{ borderTop: `1px solid ${t.border}` }}>
            <td style={{ ...labelCell, fontWeight: 600 }}>Total inflows</td>
            {columns.map((c) => <td key={c.label} style={{ ...numCell, fontWeight: 600 }}>{renderNum(c.totalInflows)}</td>)}
            <td style={{ ...numCell, fontWeight: 700, background: t.bgSurface }}>{renderNum(yearTotals.totalInflows)}</td>
          </tr>
          <tr><td colSpan={colSpanTotal} style={sectionRow}>Outflows</td></tr>
          {outflowRows.map((r) => (
            <tr key={r.key}>
              <td style={labelCellIndent}>{r.label}</td>
              {columns.map((c) => (
                <td key={c.label} style={numCellMuted}>
                  {c[r.key] === 0 ? <span style={{ color: t.textFaint }}>—</span> : <span style={{ color: '#9A3A2A' }}>({fmtUsdSource(c[r.key], cc)})</span>}
                </td>
              ))}
              <td style={{ ...numCell, background: t.bgSurface }}>
                {yearTotals[r.key] === 0 ? <span style={{ color: t.textFaint }}>—</span> : <span style={{ color: '#9A3A2A' }}>({fmtUsdSource(yearTotals[r.key], cc)})</span>}
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: `1px solid ${t.border}` }}>
            <td style={{ ...labelCell, fontWeight: 600 }}>Total outflows</td>
            {columns.map((c) => (
              <td key={c.label} style={{ ...numCell, fontWeight: 600, color: '#9A3A2A' }}>
                {c.totalOutflows === 0 ? '—' : `(${fmtUsdSource(c.totalOutflows, cc)})`}
              </td>
            ))}
            <td style={{ ...numCell, fontWeight: 700, background: t.bgSurface, color: '#9A3A2A' }}>
              ({fmtUsdSource(yearTotals.totalOutflows, cc)})
            </td>
          </tr>
          <tr style={{ borderTop: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}`, background: t.bgSurface }}>
            <td style={{ ...labelCell, fontWeight: 600, color: t.textPrimary }}>Net change</td>
            {columns.map((c) => (
              <td key={c.label} style={{ ...numCell, fontWeight: 600, color: c.netChange < 0 ? '#9A3A2A' : green }}>
                {c.netChange === 0 ? '—' : (c.netChange < 0 ? `(${fmtUsdSource(-c.netChange, cc)})` : fmtUsdSource(c.netChange, cc))}
              </td>
            ))}
            <td style={{ ...numCell, fontWeight: 700, background: t.bgCard, color: yearTotals.netChange < 0 ? '#9A3A2A' : green }}>
              {yearTotals.netChange < 0 ? `(${fmtUsdSource(-yearTotals.netChange, cc)})` : fmtUsdSource(yearTotals.netChange, cc)}
            </td>
          </tr>
          <tr style={{ background: t.bgCard }}>
            <td style={{ ...labelCell, fontWeight: 700, color: t.textPrimary, fontSize: 13 }}>Closing cash</td>
            {columns.map((c) => (
              <td key={c.label} style={{ ...numCell, fontWeight: 700, fontSize: 13 }}>{renderNum(c.closing)}</td>
            ))}
            <td style={{ ...numCell, fontWeight: 700, fontSize: 13, background: t.bgSurface }}>
              {renderNum(lastCol.closing)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function CashflowForecast() {
  const t = useTheme();
  const cc = useCurrency();
  const [activeYear, setActiveYear] = useState(2026);
  const [zoomYear, setZoomYear] = useState('all'); // 'all' | 2026 | 2027 | ...
  const [tableGranularity, setTableGranularity] = useState('quarterly'); // 'quarterly' | 'monthly' | 'annual'

  // Re-render when forecast overrides change anywhere (P&L editor → chart updates)
  const [overrideTick, bumpOverrideTick] = useReducer((x) => x + 1, 0);
  useEffect(() => subscribeOverrides(bumpOverrideTick), []);

  const cf = useMemo(() => computeCashflow(), [overrideTick]);

  // Full timeline (Dec 2025 + 24 quarters)
  const allChartPoints = useMemo(() => {
    const qe = computeQuarterEnds();
    return [
      { label: 'Dec 2025', cash: OPENING_CASH_2026 },
      ...qe.map((q) => ({ label: q.label, cash: q.cash })),
    ];
  }, [overrideTick]);

  // Zoomed view — show one year (4 quarters) + the prior Dec as anchor
  const chartForecast = useMemo(() => {
    if (zoomYear === 'all') return allChartPoints;
    const year = Number(zoomYear);
    const prior = allChartPoints.find((p) => p.label === `Dec ${year - 1}`);
    const yearPts = allChartPoints.filter((p) => p.label.endsWith(` ${year}`));
    return prior ? [prior, ...yearPts] : yearPts;
  }, [allChartPoints, zoomYear]);

  // Approved budget filtered to match zoom range
  const chartApproved = useMemo(() => {
    const labels = new Set(chartForecast.map((p) => p.label));
    return approvedCashByQuarter.filter((p) => labels.has(p.label));
  }, [chartForecast]);

  // Headline number for the title
  const ending = chartForecast[chartForecast.length - 1];

  return (
    <div style={{ marginTop: 32, fontFamily: sans }}>
      {/* Chart card — flat, hairline border, no shadow, solid bg */}
      <div style={{
        background: t.bgCard,
        border: `1px solid rgba(15, 42, 36, 0.06)`,
        borderRadius: 12,
        padding: '24px 28px 20px',
        marginBottom: 20,
      }}>
        {/* Header — eyebrow + title + legend */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{
              fontSize: 11, fontWeight: 600, color: t.textMuted,
              letterSpacing: 0.8, textTransform: 'uppercase', fontFamily: sans,
              marginBottom: 6,
            }}>
              Cash forecast
            </div>
            <div style={{
              fontSize: 22, fontWeight: 600, fontFamily: display,
              color: t.textPrimary, letterSpacing: -0.3, lineHeight: 1.15,
            }}>
              Ending{' '}
              <span style={{ fontFamily: mono, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {fmtUsdSource(ending.cash, cc)}
              </span>
              <span style={{ color: t.textMuted, fontWeight: 500 }}> · {ending.label}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, fontSize: 11, fontFamily: sans, color: t.textSecondary, alignItems: 'center', marginTop: 4 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 14, height: 1.5, background: green, borderRadius: 1 }} />
              Forecast
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <svg width={14} height={2} style={{ display: 'inline-block' }}>
                <line x1={0} y1={1} x2={14} y2={1} stroke={amber} strokeWidth={1.5} strokeDasharray="4 3" />
              </svg>
              Board-approved (FY26)
            </span>
          </div>
        </div>

        {/* Year zoom pills */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
          {[
            { key: 'all', label: 'All years' },
            ...YEARS.map((y) => ({ key: y, label: String(y) })),
          ].map((opt) => {
            const active = zoomYear === opt.key || (zoomYear === 'all' && opt.key === 'all');
            return (
              <button
                key={opt.key}
                onClick={() => setZoomYear(opt.key)}
                style={{
                  padding: '4px 12px', borderRadius: 999,
                  border: `1px solid ${active ? t.border : 'transparent'}`,
                  background: active ? t.bgSurface : 'transparent',
                  color: active ? t.textPrimary : t.textMuted,
                  fontFamily: sans, fontSize: 11,
                  fontWeight: active ? 600 : 500,
                  cursor: 'pointer',
                  transition: 'all 120ms ease',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* The chart itself */}
        <QuarterlyCashChart
          forecast={chartForecast}
          approved={chartApproved}
          currencyLabel={cc?.currency || 'USD'}
        />
      </div>

      {/* Cashflow detail table — separate card */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: sans }}>
          Cashflow detail · {tableGranularity}
        </h3>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          {/* Granularity toggle */}
          <div style={{
            display: 'inline-flex', borderRadius: 999,
            background: t.bgSurface, padding: 2,
            border: `1px solid ${t.border}`,
          }}>
            {[
              { key: 'monthly', label: 'Monthly' },
              { key: 'quarterly', label: 'Quarterly' },
              { key: 'annual', label: 'Annual' },
            ].map((opt) => {
              const active = tableGranularity === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setTableGranularity(opt.key)}
                  style={{
                    padding: '4px 12px', borderRadius: 999,
                    border: 'none',
                    background: active ? t.bgCard : 'transparent',
                    color: active ? t.textPrimary : t.textMuted,
                    fontFamily: sans, fontSize: 11,
                    fontWeight: active ? 600 : 500,
                    cursor: 'pointer',
                    boxShadow: active ? '0 1px 2px rgba(15,42,36,0.06)' : 'none',
                    transition: 'all 120ms ease',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {tableGranularity !== 'annual' && (
            <div style={{ display: 'flex', gap: 4 }}>
              {YEARS.map((y) => {
                const isActive = activeYear === y;
                return (
                  <button
                    key={y}
                    onClick={() => setActiveYear(y)}
                    style={{
                      padding: '4px 12px', borderRadius: 6,
                      border: `1px solid ${isActive ? green : t.border}`,
                      background: isActive ? '#E8F4EE' : 'transparent',
                      color: isActive ? '#0F6E56' : t.textSecondary,
                      fontFamily: sans, fontSize: 11,
                      fontWeight: isActive ? 600 : 500, cursor: 'pointer',
                    }}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <CashflowTable year={activeYear} cf={cf} granularity={tableGranularity} />
      </div>

      <div style={{ marginTop: 12, fontSize: 10, color: t.textFaint, fontFamily: sans }}>
        Operating burn comes from the expense forecast — editing the forecast will update closing cash.
        Source values in USD{cc?.currency === 'CAD' ? ` · displayed in CAD @ ${cc.fxRate?.toFixed(4)}` : ''}.
        Only the opening cash balance (Jan {YEARS[0]}, {fmtUsdSource(OPENING_CASH_2026, cc)}) is fixed.
      </div>
    </div>
  );
}
